import { useState, useCallback } from 'react';
import { X, AlertTriangle, Check } from 'lucide-react';
import { Card, CardContent, useToast } from '@mustauto/ui/web';
import type { PurchaseOrder } from '@/hooks/usePurchaseOrders';
import { formatReais } from '@/lib/utils';
import { api } from '@/lib/api';
import { useEscapeKey } from '@/hooks/useEscapeKey';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { formatApiError } from '@mustauto/utils';
import { formatStockNumber, roundStockQuantity } from '@mustauto/utils';

interface ReceiveItemLine {
  purchaseOrderItemId: string;
  inventoryItemId: string | null;
  itemName: string;
  quantityOrdered: number;
  quantityReceived: number;
  remaining: number;
  qtyToReceive: string;
  unitCost: string;
  /** Nulo para quem não vê preço de compra: aí o custo é o do pedido, sem campo. */
  originalUnitPrice: number | null;
}

interface CostDivergence {
  inventoryItemId: string;
  itemName: string;
  oldCost: number;
  newCost: number;
  updated: boolean;
  updating: boolean;
}

interface ReceiveModalProps {
  order: PurchaseOrder;
  onSubmit: (data: {
    id: string;
    items: {
      purchaseOrderItemId: string;
      quantityReceived: number;
      unitCost?: number;
    }[];
    notes?: string | null;
  }) => Promise<void>;
  onClose: () => void;
  isSubmitting: boolean;
}

export function ReceiveModal({ order, onSubmit, onClose, isSubmitting }: ReceiveModalProps) {
  useEscapeKey(onClose);
  const trapRef = useFocusTrap<HTMLDivElement>();
  const { toast } = useToast();
  const [lines, setLines] = useState<ReceiveItemLine[]>(
    (order.items ?? []).map((item) => ({
      purchaseOrderItemId: item.id,
      inventoryItemId: item.inventoryItemId,
      itemName: item.itemName,
      quantityOrdered: item.quantityOrdered,
      quantityReceived: item.quantityReceived,
      // Arredondado: 1 − 0,7 em float é 0,30000000000000004, que a API
      // (que arredonda) recusaria como "mais do que falta".
      remaining: roundStockQuantity(item.quantityOrdered - item.quantityReceived),
      qtyToReceive: String(roundStockQuantity(item.quantityOrdered - item.quantityReceived)),
      unitCost: item.unitPrice == null ? '' : String(item.unitPrice),
      originalUnitPrice: item.unitPrice,
    }))
  );
  // Sem ver o preço de compra (Fase 3 das permissões), quem recebe confere a
  // quantidade; o custo é o do pedido, que a API aplica.
  const priceHidden = (order.items ?? []).some((i) => i.unitPrice == null);
  const [notes, setNotes] = useState('');
  const [phase, setPhase] = useState<'form' | 'done'>('form');
  const [divergences, setDivergences] = useState<CostDivergence[]>([]);

  const updateLine = useCallback((idx: number, field: 'qtyToReceive' | 'unitCost', value: string) => {
    setLines((prev) => prev.map((l, i) => i === idx ? { ...l, [field]: value } : l));
  }, []);

  const handleUpdateCost = useCallback(async (idx: number) => {
    const d = divergences[idx];
    setDivergences((prev) => prev.map((item, i) => i === idx ? { ...item, updating: true } : item));
    try {
      await api.patch(`/inventory/${d.inventoryItemId}`, { unitCost: d.newCost });
      setDivergences((prev) => prev.map((item, i) => i === idx ? { ...item, updated: true, updating: false } : item));
    } catch (err) {
      setDivergences((prev) => prev.map((item, i) => i === idx ? { ...item, updating: false } : item));
      toast({
        type: 'error',
        title: 'Falha ao atualizar custo',
        description: formatApiError(err, 'Não foi possível atualizar o custo do item. Tente novamente.'),
      });
    }
  }, [divergences, toast]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const receivedLines = lines.filter((l) => Number(l.qtyToReceive) > 0);
      const itemsToReceive = receivedLines.map((l) => ({
        purchaseOrderItemId: l.purchaseOrderItemId,
        quantityReceived: Number(l.qtyToReceive),
        ...(priceHidden ? {} : { unitCost: Number(l.unitCost) }),
      }));
      if (itemsToReceive.length === 0) return;
      await onSubmit({
        id: order.id,
        items: itemsToReceive,
        notes: notes || null,
      });

      // Check cost divergences (>5% difference)
      const costDiffs: CostDivergence[] = [];
      for (const line of receivedLines) {
        if (!line.inventoryItemId || line.originalUnitPrice == null) continue;
        const receivedCost = Number(line.unitCost);
        const originalCost = line.originalUnitPrice;
        if (originalCost === 0 && receivedCost === 0) continue;
        const pctDiff = originalCost > 0
          ? Math.abs(receivedCost - originalCost) / originalCost
          : 1;
        if (pctDiff > 0.05) {
          costDiffs.push({
            inventoryItemId: line.inventoryItemId,
            itemName: line.itemName,
            oldCost: originalCost,
            newCost: receivedCost,
            updated: false,
            updating: false,
          });
        }
      }

      if (costDiffs.length > 0) {
        setDivergences(costDiffs);
        setPhase('done');
      } else {
        onClose();
      }
    },
    [lines, notes, order.id, onSubmit, onClose, priceHidden],
  );

  const isPartial = lines.some((l) => Number(l.qtyToReceive) < l.remaining);
  // Conta LINHAS: somar quantidades misturaria litro com unidade.
  const receivingLines = lines.filter((l) => (Number(l.qtyToReceive) || 0) > 0).length;

  if (phase === 'done') {
    return (
      <div ref={trapRef} role="dialog" aria-modal="true" className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <Card className="w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <AlertTriangle size={18} className="text-amber-500 dark:text-amber-400" />
                <h3 className="text-base font-semibold">Divergência de custo</h3>
              </div>
              <button onClick={onClose} aria-label="Fechar" className="rounded-md p-1 hover:bg-muted"><X size={18} /></button>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              O custo real de alguns itens difere do cadastro no estoque. Deseja atualizar?
            </p>
            <div className="space-y-2">
              {divergences.map((d, idx) => (
                <div key={d.inventoryItemId} className="flex items-center justify-between rounded-lg border px-3 py-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{d.itemName}</p>
                    <p className="text-xs text-muted-foreground">
                      Cadastro: {formatReais(d.oldCost)} → Recebido: {formatReais(d.newCost)}
                    </p>
                  </div>
                  {d.updated ? (
                    <span className="flex items-center gap-1 text-xs font-medium text-emerald-600 shrink-0 ml-2 dark:text-emerald-400">
                      <Check size={14} /> Atualizado
                    </span>
                  ) : (
                    <button
                      onClick={() => handleUpdateCost(idx)}
                      disabled={d.updating}
                      className="shrink-0 ml-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-1 text-xs font-medium text-amber-700 hover:bg-amber-100 dark:bg-amber-950/40 dark:border-amber-900/60 dark:text-amber-200 dark:hover:bg-amber-950/60 transition-colors disabled:opacity-50"
                    >
                      {d.updating ? 'Atualizando...' : 'Atualizar'}
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-4">
              <button
                onClick={onClose}
                className="rounded-lg border px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                Fechar
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div ref={trapRef} role="dialog" aria-modal="true" className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <Card className="w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-base font-semibold">Registrar Recebimento</h3>
              <p className="text-sm text-muted-foreground">OC #{order.number}</p>
            </div>
            <button onClick={onClose} aria-label="Fechar" className="rounded-md p-1 hover:bg-muted"><X size={18} /></button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="rounded-lg border overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Item</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground w-20">Pedido</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground w-20">Recebido</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground w-20">Restante</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground w-24">Receber agora</th>
                    {!priceHidden && <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground w-28">Custo real</th>}
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line, idx) => (
                    <tr key={line.purchaseOrderItemId} className="border-b last:border-0">
                      <td className="px-3 py-2 font-medium">{line.itemName}</td>
                      <td className="px-3 py-2 text-right font-mono">{formatStockNumber(line.quantityOrdered)}</td>
                      <td className="px-3 py-2 text-right font-mono">{formatStockNumber(line.quantityReceived)}</td>
                      <td className="px-3 py-2 text-right font-mono">{formatStockNumber(line.remaining)}</td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          value={line.qtyToReceive}
                          onChange={(e) => updateLine(idx, 'qtyToReceive', e.target.value)}
                          min={0}
                          max={line.remaining}
                          step="0.001"
                          inputMode="decimal"
                          className="w-full rounded border border-input bg-background px-2 py-1 text-sm text-right"
                        />
                      </td>
                      {!priceHidden && (
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            value={line.unitCost}
                            onChange={(e) => updateLine(idx, 'unitCost', e.target.value)}
                            min={0}
                            step="0.01"
                            className="w-full rounded border border-input bg-background px-2 py-1 text-sm text-right"
                          />
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {isPartial && (
              <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-700 dark:bg-amber-950/20 dark:border-amber-900/60 dark:text-amber-200">
                Recebimento parcial: o que não for recebido agora continua pendente na ordem de compra.
              </div>
            )}

            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Observações</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                maxLength={1000}
                rows={2}
                placeholder="Ex: NF 12345, conferência OK"
                className="rounded-md border border-input bg-background px-3 py-2 text-sm resize-none"
              />
            </div>

            <div className="flex items-center gap-3">
              <button
                type="submit"
                disabled={isSubmitting || receivingLines === 0}
                className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-600 transition-colors disabled:opacity-50"
              >
                {isSubmitting ? 'Registrando...' : `Confirmar recebimento (${receivingLines} ${receivingLines === 1 ? 'item' : 'itens'})`}
              </button>
              <button type="button" onClick={onClose}
                className="rounded-lg border px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                Cancelar
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
